import { TTenantConfig, TTokenData } from '@shared/types';
import tenantConfig, { NOT_ALLOWED_REDIRECT } from '../../tenantConfig';
import useUserStore from '@core/stores/useUserStore';
import { logger } from '@shared/logger';
import { getCookie, setCookie } from 'tiny-cookie';

/**
 * Check if the current page is being loaded inside an iframe.
 *
 * This function attempts to determine if the current window is the top window or if it's nested inside an iframe.
 * If the window is inside an iframe, it returns true, otherwise, it returns false.
 *
 * @returns True if the current page is in an iframe, otherwise false.
 */
export const inIframe = () => {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
};

/**
 * Dynamically add one or more scripts to the page.
 *
 * This function ensures that the provided script URLs are not already included in the document and then adds them to the document head.
 *
 * @param {Array} scriptUrls - An array of objects representing script URLs to be added.
 * @param {string} scriptUrls.src - The source URL of the script.
 * @param {string} [scriptUrls.type='text/javascript'] - The type of the script (default is 'text/javascript').
 * @param {boolean} [scriptUrls.noModule=false] - Whether to add the 'noModule' attribute to the script tag (default is false).
 */
export const addScripts = (
    scriptUrls: {
        src: string;
        type?: string;
        noModule?: boolean;
    }[],
): void => {
    scriptUrls.forEach(({ src, type = 'text/javascript', noModule = false }) => {
        if (!document.querySelector(`script[src="${src}"]`)) {
            const script = document.createElement('script');
            script.src = src;
            script.type = type;
            if (noModule) {
                script.noModule = true;
            }
            document.head.appendChild(script);
        }
    });
};

/**
 * Add Eon UI components to the page by adding the necessary script tags.
 *
 * This function specifically adds the required Eon UI scripts for the page to use Eon UI components. The scripts are loaded
 * from a CDN, with support for both 'module' and 'noModule' types.
 */
export const addEonUiComponents = (): void => {
    addScripts([
        {
            src: 'https://web-ui.eon.com/cdn/components/4.5.4/eon/eon-ui-components/eon-ui-components.esm.js',
            type: 'module',
        },
        {
            src: 'https://web-ui.eon.com/cdn/components/4.5.4/eon/eon-ui-components/eon-ui-components.js',
            noModule: true,
        },
    ]);
};

/**
 * Set the favicon of the page.
 *
 * This function dynamically sets the favicon by adding or updating a link element in the document head.
 *
 * @param {string} iconUrl - The URL of the favicon to be set.
 */
export const setFavIcon = (iconUrl: string): void => {
    const link = (document.querySelector('link[rel="icon"]') ||
        document.createElement('link')) as HTMLLinkElement;
    link.rel = 'icon';
    link.href = iconUrl;
    document.head.appendChild(link);
};

/**
 * shortens a string to a fixed length, using a hash function.
 * The hash function is a simple implementation of the djb2 algorithm.
 *
 * @param str
 * @param length
 */

export function hashString(str: string, length = 6) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash * 31 + str.charCodeAt(i)) % 0xffffffff;
    }
    return Math.abs(hash).toString(36).slice(0, length);
}

/**
 * Retrieve the tenant configuration from the token data.
 *
 * This function searches for the tenant configuration based on the groups in the provided token data.
 *
 * @param {TTokenData} tokenData - The token data containing user group information.
 * @returns {TTenantConfig | undefined} The matching tenant configuration, or undefined if no matching configuration is found.
 */
export const getTenantConfigFromToken = (tokenData: TTokenData): TTenantConfig | undefined => {
    if (!tokenData) {
        return;
    }

    // if the user has no groups, redirect to eon-gpt
    try {
        return tenantConfig.find((config) =>
            config.groups.some((group) => tokenData.groups!.includes(group)),
        );
    } catch (error) {
        return;
    }
};

/**
 * Find the Business Unit (BU) group from the token data.
 *
 * This function searches for a Business Unit (BU) group based on the provided token data and tenant configuration.
 * If a match is found, the corresponding group name is returned.
 *
 * @param {TTokenData} tokenData - The token data containing user group information.
 * @param {TTenantConfig | undefined} tenantConfig - The tenant configuration object.
 * @returns {string | null} The BU group name, or null if no matching group is found.
 */
export const getBUGroupFromToken = (
    tokenData: TTokenData,
    tenantConfig: TTenantConfig | undefined,
): string | null => {
    if (!tenantConfig) {
        return null;
    }
    try {
        return tenantConfig.groups.find((group) => tokenData.groups!.includes(group)) || null;
    } catch (error) {
        return null;
    }
};

/**
 * Truncate a string to a specified length.
 *
 * This function truncates a string to a specified length and appends an ellipsis if the string exceeds the length.
 *
 * @param {string} str - The string to truncate.
 * @param {number} [maxLength=50] - The maximum length of the truncated string (default is 50).
 * @returns {string | undefined} The truncated string, or undefined if the input string is not provided.
 */
export const truncateString = (
    str?: string | null,
    maxLength: number = 50,
): string | null | undefined => {
    if (!str) return str; // Return undefined if the string is not provided
    return str.length > maxLength ? str.slice(0, maxLength) + '...' : str;
};

export const getEmbeddedAssistantUrl = (
    publishUrl?: string | null,
    suffix: string = '-embedded',
): string | undefined => {
    if (!publishUrl?.trim()) {
        return undefined;
    }

    try {
        const parsedUrl = new URL(publishUrl, window.location.origin);
        const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);

        if (pathSegments.length === 0) {
            return undefined;
        }

        const [assistantPath, ...remainingSegments] = pathSegments;
        const normalizedAssistantPath = assistantPath.endsWith(suffix)
            ? assistantPath
            : `${assistantPath}${suffix}`;

        parsedUrl.pathname = `/${[normalizedAssistantPath, ...remainingSegments].join('/')}`;
        return parsedUrl.toString();
    } catch {
        return undefined;
    }
};

export const noPermissionRedirect = () => {
    // If the user doesn't have the required UI config but has a token, log an error
    logger.warn('utils', 1, 'User has no EN GenAi permissions', useUserStore.getState().tokenData);

    // only do a redirect of no "debug" parameters is set in the URL
    if (
        ['main', 'qas'].includes(import.meta.env.MODE) &&
        !new URLSearchParams(window.location.search).has('debug')
    ) {
        // Redirect to the NOT_ALLOWED_REDIRECT URL if the user has a token but no UI config only in shared environments
        window.location.href = NOT_ALLOWED_REDIRECT;
    }
};

/**
 * Refresh the page after an error.
 * This function is used to refresh the page after an error occurs, but only once to prevent an infinite loop.
 *
 * @returns {boolean} True if the page is refreshed, otherwise false.
 *
 */

export const refreshAfterError = (): boolean => {
    if (!getCookie('refreshOnce')) {
        // Mark that we already handled this transient auth error so callers can stop retry loops.
        setCookie('refreshOnce', 'true', { expires: '30s' });
        return true;
    }
    return false;
};
